import { useEffect, useState } from 'react';
import SelectField from '../common/SelectField.js';
import Button from '../common/Button.js';
import { Plus, X } from 'lucide-react';

const fieldClass =
  'w-full min-w-0 px-3 sm:px-4 py-2.5 sm:py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm bg-white';

const hubLabel = (hub) =>
  `${hub.location}${hub.location_code ? ` (${hub.location_code})` : ''}`;

/**
 * Crossing hubs on a route. Each hub lists the locations goods can cross to.
 */
const RouteCrossingFields = ({ hubs, formData, setFormData }) => {
  const [hubPick, setHubPick] = useState('');
  const [destIds, setDestIds] = useState([]);

  const pathIds = [
    formData.from_location_hub_id,
    ...(formData.via_location_ids || []),
    formData.to_location_hub_id,
  ].filter((id) => id !== '' && id != null).map((id) => String(id));

  const childBranchesOf = (hubId) => hubs.filter(
    (hub) => hub?.parent_hub_id != null && String(hub.parent_hub_id) === String(hubId),
  );

  const upsertStop = (hubId, destinationIds) => {
    if (!hubId) return;
    setFormData((prev) => {
      const rest = (prev.crossing_stops || []).filter((stop) => String(stop.hub_id) !== String(hubId));
      if (!destinationIds.length) {
        return { ...prev, crossing_stops: rest };
      }
      const hub = hubs.find((item) => String(item.id) === String(hubId));
      const destinations = destinationIds
        .map((id) => hubs.find((item) => String(item.id) === String(id)))
        .filter(Boolean)
        .map((item) => ({
          id: item.id,
          location: item.location,
          location_code: item.location_code,
        }));
      const children = childBranchesOf(hubId);
      return {
        ...prev,
        crossing_stops: [
          ...rest,
          {
            hub_id: Number(hubId),
            hub_name: hub?.location || '',
            destination_hub_ids: destinationIds.map((id) => Number(id)),
            destinations,
            location_hub_ids: [Number(hubId), ...children.map((item) => Number(item.id))],
            child_branches: children.map((item) => ({
              id: item.id,
              location: item.location,
              location_code: item.location_code,
            })),
          },
        ],
      };
    });
  };

  useEffect(() => {
    if (!pathIds.length) return undefined;
    const allowed = new Set(pathIds);
    setFormData((prev) => {
      const current = prev.crossing_stops || [];
      const next = current.filter((stop) => allowed.has(String(stop.hub_id)));
      if (next.length === current.length) return prev;
      return { ...prev, crossing_stops: next };
    });
    return undefined;
    // Only drop a stop when its hub leaves the route path.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathIds.join('|')]);

  const used = new Set((formData.crossing_stops || []).map((stop) => String(stop.hub_id)));
  const pathHubs = pathIds
    .map((id) => hubs.find((hub) => String(hub.id) === id))
    .filter(Boolean)
    .filter((hub) => !used.has(String(hub.id)));

  const destinationHubs = hubs.filter((hub) => {
    if (!hub?.id) return false;
    if (String(hub.id) === String(hubPick)) return false;
    return true;
  });

  const toggleDest = (id) => {
    const next = destIds.some((item) => String(item) === String(id))
      ? destIds.filter((item) => String(item) !== String(id))
      : [...destIds, Number(id)];
    setDestIds(next);
    upsertStop(hubPick, next);
  };

  const addStop = () => {
    if (!hubPick || destIds.length === 0) return;
    upsertStop(hubPick, destIds);
    setHubPick('');
    setDestIds([]);
  };

  const removeStop = (hubId) => {
    setFormData((prev) => ({
      ...prev,
      crossing_stops: (prev.crossing_stops || []).filter(
        (stop) => String(stop.hub_id) !== String(hubId),
      ),
    }));
  };

  return (
    <div className="sm:col-span-2 space-y-3">
      <div>
        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
          Crossing hubs
        </label>
        <p className="text-[11px] sm:text-xs text-gray-500 mb-2">
          Optional. Pick a stop on this route, then the locations goods can cross to from that hub.
          One route can have more than one crossing hub.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <SelectField
          value={hubPick}
          onChange={(e) => {
            setHubPick(e.target.value);
            setDestIds([]);
          }}
          className={fieldClass}
        >
          <option value="">Crossing hub…</option>
          {pathHubs.map((hub) => (
            <option key={hub.id} value={hub.id}>
              {hubLabel(hub)}
            </option>
          ))}
        </SelectField>
        <Button
          type="button"
          variant="secondary"
          icon={Plus}
          mobileIconOnly
          mobileLabel="Add"
          onClick={addStop}
          disabled={!hubPick || destIds.length === 0}
        >
          Add crossing
        </Button>
      </div>

      {hubPick ? (
        <div className="max-h-36 overflow-y-auto rounded-xl border border-gray-200 p-2 space-y-1">
          {destinationHubs.map((hub) => (
            <label key={hub.id} className="flex items-center gap-2 text-xs sm:text-sm text-gray-800">
              <input
                type="checkbox"
                checked={destIds.some((id) => String(id) === String(hub.id))}
                onChange={() => toggleDest(hub.id)}
              />
              <span className="truncate">{hubLabel(hub)}</span>
            </label>
          ))}
        </div>
      ) : null}

      {(formData.crossing_stops || []).length === 0 ? (
        <p className="text-[11px] sm:text-xs text-gray-500">No crossing hubs on this route.</p>
      ) : (
        <ul className="space-y-1.5">
          {(formData.crossing_stops || []).map((stop) => {
            const hub = hubs.find((item) => String(item.id) === String(stop.hub_id));
            const destNames = (stop.destination_hub_ids || []).map((id) => {
              const match = hubs.find((item) => String(item.id) === String(id));
              return match ? match.location : id;
            });
            return (
              <li
                key={stop.hub_id}
                className="flex items-start justify-between gap-2 px-2.5 sm:px-3 py-2 border border-amber-200 rounded-xl bg-amber-50 min-w-0"
              >
                <span className="text-xs sm:text-sm text-gray-800 min-w-0 break-words">
                  <span className="font-semibold">{hub ? hub.location : stop.hub_name || stop.hub_id}</span>
                  <span className="mt-1 flex flex-wrap gap-1">
                    {destNames.map((name) => (
                      <span
                        key={name}
                        className="inline-flex px-2 py-0.5 rounded-full bg-white border border-amber-200 text-[11px]"
                      >
                        {name}
                      </span>
                    ))}
                  </span>
                </span>
                <button
                  type="button"
                  onClick={() => removeStop(stop.hub_id)}
                  className="p-1.5 rounded-lg hover:bg-red-50 text-red-600 shrink-0"
                  title="Remove"
                >
                  <X className="w-4 h-4" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default RouteCrossingFields;
